import type {
  ProviderId,
  ScenarioContent,
  ScenarioSourceItem,
  InteractionMode,
} from "@/lib/powtoon-be/types";

/**
 * Declarative description of how a provider lays out its scenario content.
 *
 * This is the single source of truth for "provider field knowledge" — where a
 * provider's block lives, where its interaction mode / questionnaire versions /
 * source inputs are read from, and which sibling blocks have to be stripped
 * when a scenario switches provider.
 *
 * Adding a provider means adding one entry here instead of touching every
 * call site that branches on `content.provider`.
 */
export type ProviderContentSpec = {
  id: ProviderId;
  /**
   * Key of the provider-specific block inside `ScenarioContent`.
   * LiveAvatar predates the pattern and uses `context_block`.
   */
  blockKey: "context_block" | "pal" | "did" | "kaltura";
  /**
   * Top-level content fields owned exclusively by this provider. Used to
   * build the strip list for the other providers, so nothing has to be
   * hand-listed in `toContent`.
   */
  ownedTopLevelFields?: readonly string[];
  /**
   * Fields on the provider block holding ids provisioned upstream (agent,
   * pal, context, avatar). Cleared when a scenario is duplicated so the copy
   * provisions its own remote resources instead of sharing them.
   */
  provisionedIdFields?: readonly string[];
};

export const PROVIDER_SPECS: Record<ProviderId, ProviderContentSpec> = {
  liveavatar: {
    id: "liveavatar",
    blockKey: "context_block",
    ownedTopLevelFields: ["avatar_id", "voice_id", "context_id", "mode", "language"],
    provisionedIdFields: ["liveavatar_context_id"],
  },
  tavus: {
    id: "tavus",
    blockKey: "pal",
    ownedTopLevelFields: ["language"],
    // Knowledge documents are provisioned per scenario too — a duplicate must
    // mint its own so deleting one scenario can't strip the other's retrieval.
    provisionedIdFields: ["pal_id", "knowledge_documents", "knowledge_document_id"],
  },
  did: {
    id: "did",
    blockKey: "did",
    ownedTopLevelFields: ["language"],
    provisionedIdFields: ["agent_id"],
  },
  kaltura: { id: "kaltura", blockKey: "kaltura", provisionedIdFields: ["avatar_id"] },
};

const ALL_SPECS = Object.values(PROVIDER_SPECS);

/** Provider of a content object, defaulting to `liveavatar` when unset. */
export function providerOf(content: ScenarioContent | undefined | null): ProviderId {
  return content?.provider ?? "liveavatar";
}

export function specOf(content: ScenarioContent | undefined | null): ProviderContentSpec {
  return PROVIDER_SPECS[providerOf(content)] ?? PROVIDER_SPECS.liveavatar;
}

/**
 * Shape shared by every provider block (and `context_block`) for the fields
 * the app reads generically.
 */
type CommonBlockFields = {
  interaction_mode?: InteractionMode;
  questionnaire_ids?: string[];
  roleplay_ids?: string[];
  roleplay_scenario_id?: string;
  inputs?: ScenarioSourceItem[];
};

/** The active provider's content block, read through the spec. */
export function providerBlockOf(
  content: ScenarioContent | undefined | null,
): CommonBlockFields | undefined {
  if (!content) return undefined;
  return content[specOf(content).blockKey] as CommonBlockFields | undefined;
}

/** Questionnaire versions for this scenario, newest first. */
export function questionnaireIdsOfContent(
  content: ScenarioContent | undefined | null,
): string[] {
  return providerBlockOf(content)?.questionnaire_ids ?? [];
}

/** Role-play artifact versions for this scenario, newest first. */
export function roleplayIdsOfContent(
  content: ScenarioContent | undefined | null,
): string[] {
  return providerBlockOf(content)?.roleplay_ids ?? [];
}

/**
 * Id of the role-play scenario a session plays (and is scored against). The
 * Powtoon BE resolves the grading rubric by matching this id against the
 * `live_avatar_roleplay` object's `scenarios[]`, so it must never be the
 * project/scenario id. Written by `saveScenario` from the persisted artifact.
 */
export function playedRolePlayScenarioIdOf(
  content: ScenarioContent | undefined | null,
): string | undefined {
  return providerBlockOf(content)?.roleplay_scenario_id;
}

/** Interaction mode for this scenario, regardless of provider. */
export function interactionModeOf(
  content: ScenarioContent | undefined | null,
): InteractionMode {
  return providerBlockOf(content)?.interaction_mode ?? "conversational";
}

/** Raw source inputs (uploads/urls/text) for this scenario. */
export function inputsOf(
  content: ScenarioContent | undefined | null,
): ScenarioSourceItem[] {
  return providerBlockOf(content)?.inputs ?? [];
}

/**
 * `undefined`-valued patch that clears every block and top-level field owned
 * by the *other* providers. Spread into the object returned by `toContent` so
 * switching provider can never leave stale values behind.
 */
export function strippedForeignFields(provider: ProviderId): Record<string, undefined> {
  const mine = PROVIDER_SPECS[provider];
  const patch: Record<string, undefined> = {};
  for (const spec of ALL_SPECS) {
    if (spec.id === provider) continue;
    patch[spec.blockKey] = undefined;
    for (const field of spec.ownedTopLevelFields ?? []) patch[field] = undefined;
  }
  // A provider may share a top-level field name with one it strips
  // (e.g. LiveAvatar owns `voice_id`); never clear our own fields.
  for (const field of mine.ownedTopLevelFields ?? []) delete patch[field];
  delete patch[mine.blockKey];
  return patch;
}

/**
 * Content for a duplicated scenario: keeps the authoring fields but clears
 * every upstream-provisioned id and the questionnaire version history, so the
 * copy provisions its own remote resources on first save. Provider-agnostic.
 */
export function contentForDuplicate(
  content: ScenarioContent | undefined | null,
): ScenarioContent {
  const spec = specOf(content);
  const base = { ...(content ?? {}) } as ScenarioContent;
  const block = base[spec.blockKey] as Record<string, unknown> | undefined;
  if (block) {
    const nextBlock: Record<string, unknown> = {
      ...block,
      questionnaire_ids: undefined,
      roleplay_ids: undefined,
    };
    for (const field of spec.provisionedIdFields ?? []) nextBlock[field] = undefined;
    base[spec.blockKey] = nextBlock as never;
  }
  // Runtime mirror of the LiveAvatar context id, provisioned on save.
  if (spec.id === "liveavatar") base.context_id = undefined;
  return base;
}